import type {
  RadioChangeEvent,
  RadioGroupProps as AntRadioGroupProps
} from 'antd';
import { Radio as AntRadio } from 'antd';
import React from 'react';
import styled, { css } from 'styled-components';
import { theme } from '../../theme/semanticTokens';
import { RadioButton, RadioButtonProps } from '../RadioButton/RadioButton';

export type { RadioChangeEvent };

export interface RadioGroupProps extends Omit<AntRadioGroupProps, 'options'> {
  options?: Array<RadioButtonProps>;
  onChange?: (value: RadioChangeEvent) => void;
}

const StyledRadioGroup = styled(AntRadio.Group)<{ $isButtonType?: boolean }>`
  ${({ $isButtonType }) =>
    !$isButtonType
      ? css`
          display: flex;
          flex-direction: row;
          gap: ${theme.dime.spacing.m};
        `
      : css`
          .ant-radio-button-wrapper {
            border-color: ${theme.dime.colors.border.static};
            font-size: ${theme.dime.fonts.sizes.sm};

            &:first-child {
              border-radius: ${theme.dime.radius.m} 0 0 ${theme.dime.radius.m};
            }

            &:last-child {
              border-radius: 0 ${theme.dime.radius.m} ${theme.dime.radius.m} 0;
            }

            &:not(.ant-radio-button-wrapper-checked):hover {
              color: ${theme.dime.colors.action.primaryHover};
            }
          }

          .ant-radio-button-wrapper-checked {
            background: ${theme.dime.colors.action.primary};
            border-color: ${theme.dime.colors.border.interactiveSelected};
            color: ${theme.dime.colors.text.inverse};

            &:first-child {
              border-radius: ${theme.dime.radius.m} 0 0 ${theme.dime.radius.m};
            }

            &:last-child {
              border-radius: 0 ${theme.dime.radius.m} ${theme.dime.radius.m} 0;
            }

            &:hover {
              background: ${theme.dime.colors.action.primaryHover};
              border-color: ${theme.dime.colors.action.primaryHover};
              color: ${theme.dime.colors.text.inverse};
            }

            &::before {
              background-color: ${theme.dime.colors.border.interactiveSelected};
            }
          }
        `}
`;

export const RadioGroup = React.forwardRef<HTMLDivElement, RadioGroupProps>(
  ({ options, children, onChange, optionType, ...props }, ref) => {
    const isButtonType = optionType === 'button';
    return (
      <StyledRadioGroup
        ref={ref}
        onChange={onChange}
        optionType={optionType}
        $isButtonType={isButtonType}
        {...props}
      >
        {options?.map((option) =>
          isButtonType ? (
            <AntRadio.Button
              key={String(option.value)}
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </AntRadio.Button>
          ) : (
            <RadioButton {...option} key={String(option.value)} />
          )
        ) || children}
      </StyledRadioGroup>
    );
  }
);

RadioGroup.displayName = 'RadioGroup';
